/* ═══════════════════════════════════════════════════════════
   Telas e modais de: fontes de recurso, patrocínios (contrato,
   parcelas, contrapartidas, anexos), reembolsos e meta anual.
   ═══════════════════════════════════════════════════════════ */

import React, { useState } from 'react';
import {
  X, Plus, Trash2, Paperclip, Wallet, Handshake, Target,
  Undo2, CheckCircle, Clock, AlertTriangle, Download, Coins,
  CalendarClock, FileText, Pencil, RotateCcw,
} from 'lucide-react';
import {
  Fonte, SaldoFonte, Patrocinio, Parcela, Contrapartida, Anexo, Meta,
  ResumoAno, FinanceConfig, MOEDAS, fmt, fmtData, uploadAnexo, linkAnexo,
  LABEL_TIPO_FONTE, LABEL_TIPO_CONTRAPARTIDA, CORES_STATUS_PATROCINIO,
} from './tipos';

const inputCls =
  'w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400';
const inputSm =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400';
// Mesma caixa, sem largura: para linhas onde a largura vem do flex/grid.
// Combinar `w-32 ${inputSm}` NÃO funciona — no CSS do Tailwind o w-full do
// inputSm vem depois e vence, então todo campo virava 100% e o vizinho
// flex-1 encolhia até desaparecer (era o campo da parcela sumido).
const inputSmLivre =
  'px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400';

const Modal: React.FC<{
  titulo: string;
  icone: React.ReactNode;
  largura?: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ titulo, icone, largura = 'max-w-lg', onClose, children }) => (
  <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-8">
    <div className={`bg-white rounded-2xl shadow-2xl w-full ${largura} mx-4 overflow-hidden`}>
      <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg flex items-center gap-2">{icone} {titulo}</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      {children}
    </div>
  </div>
);

const Vazio: React.FC<{ icone: React.ReactNode; titulo: string; texto: string }> = ({ icone, titulo, texto }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
    <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4 text-blue-500">{icone}</div>
    <p className="text-gray-900 font-semibold">{titulo}</p>
    <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">{texto}</p>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   MODAL — FONTE DE RECURSO
   ═══════════════════════════════════════════════════════════ */

export const FonteModal: React.FC<{
  fonte?: Fonte | null;
  config: FinanceConfig;
  onSave: (dados: Record<string, unknown>) => void;
  onClose: () => void;
}> = ({ fonte, config, onSave, onClose }) => {
  const [nome, setNome] = useState(fonte?.nome || '');
  const [tipo, setTipo] = useState<Fonte['tipo']>(fonte?.tipo || 'pessoal');
  const [moeda, setMoeda] = useState(fonte?.moeda || config.moedaBase);
  const [saldoInicial, setSaldoInicial] = useState(String(fonte?.saldoInicial ?? ''));
  const [cor, setCor] = useState(fonte?.cor || '#2563eb');
  const [ativo, setAtivo] = useState(fonte?.ativo !== false);
  const [observacoes, setObservacoes] = useState(fonte?.observacoes || '');
  // só assume o padrão do tipo enquanto a fonte é nova
  const [reembolsavelPadrao, setReembolsavelPadrao] = useState(
    fonte ? !!fonte.reembolsavelPadrao : true
  );
  const [tocouReembolso, setTocouReembolso] = useState(!!fonte);

  const trocarTipo = (novo: Fonte['tipo']) => {
    setTipo(novo);
    if (!tocouReembolso) setReembolsavelPadrao(novo === 'pessoal');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) return;
    onSave({
      nome: nome.trim(), tipo, moeda,
      saldoInicial: parseFloat(saldoInicial) || 0,
      reembolsavelPadrao, cor, ativo, observacoes,
    });
  };

  return (
    <Modal titulo={fonte ? 'Editar Fonte' : 'Nova Fonte de Recurso'} icone={<Wallet className="w-5 h-5" />} onClose={onClose}>
      <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
        <p className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-xl p-3">
          A fonte responde <strong>de onde saiu</strong> (ou entrou) o dinheiro: seu bolso pessoal,
          o caixa da loja, a conta de um patrocinador. É o que permite saber, no fim do ano,
          quanto de cada bolso você usou.
        </p>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
          <input type="text" value={nome} onChange={e => setNome(e.target.value)} className={inputCls}
            placeholder="Ex: Meu dinheiro pessoal" required />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select value={tipo} onChange={e => trocarTipo(e.target.value as Fonte['tipo'])} className={inputCls}>
              {Object.entries(LABEL_TIPO_FONTE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Moeda</label>
            <select value={moeda} onChange={e => setMoeda(e.target.value)} className={inputCls}>
              {config.moedas.map(m => (
                <option key={m} value={m}>{m} — {MOEDAS[m]?.nome || m}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Saldo inicial</label>
            <input type="number" step="0.01" value={saldoInicial} onChange={e => setSaldoInicial(e.target.value)}
              className={inputCls} placeholder="0,00" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cor</label>
            <input type="color" value={cor} onChange={e => setCor(e.target.value)}
              className="w-full h-[42px] px-1 border border-gray-200 rounded-xl cursor-pointer" />
          </div>
        </div>
        <label className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50">
          <input type="checkbox" checked={reembolsavelPadrao} className="mt-0.5"
            onChange={e => { setReembolsavelPadrao(e.target.checked); setTocouReembolso(true); }} />
          <span className="text-sm">
            <span className="font-medium text-gray-800">Gastos por esta fonte precisam ser devolvidos</span>
            <span className="block text-xs text-gray-500">
              Toda despesa paga por aqui já nasce marcada como reembolso a receber.
            </span>
          </span>
        </label>
        <label className="flex items-center gap-3 text-sm text-gray-700">
          <input type="checkbox" checked={ativo} onChange={e => setAtivo(e.target.checked)} />
          Fonte ativa
        </label>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
          <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)}
            className={`${inputCls} resize-none`} rows={2} />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancelar</button>
          <button type="submit" className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:shadow-lg">Salvar</button>
        </div>
      </form>
    </Modal>
  );
};

/* ═══════════════════════════════════════════════════════════
   MODAL — PATROCÍNIO / CONTRATO
   ═══════════════════════════════════════════════════════════ */

export const PatrocinioModal: React.FC<{
  patrocinio?: Patrocinio | null;
  config: FinanceConfig;
  fontes: Fonte[];
  userEmail: string;
  onSave: (dados: Record<string, unknown>) => void;
  onClose: () => void;
}> = ({ patrocinio, config, fontes, userEmail, onSave, onClose }) => {
  const [patrocinador, setPatrocinador] = useState(patrocinio?.patrocinador || '');
  const [contato, setContato] = useState(patrocinio?.contato || '');
  const [moeda, setMoeda] = useState(patrocinio?.moeda || config.moedaBase);
  const [valorTotal, setValorTotal] = useState(String(patrocinio?.valorTotal ?? ''));
  const [cotacao, setCotacao] = useState(String(patrocinio?.cotacao ?? ''));
  const [inicio, setInicio] = useState(patrocinio?.inicio || '');
  const [fim, setFim] = useState(patrocinio?.fim || '');
  const [status, setStatus] = useState<Patrocinio['status']>(patrocinio?.status || 'ativo');
  const [fonteId, setFonteId] = useState(patrocinio?.fonteId || '');
  const [observacoes, setObservacoes] = useState(patrocinio?.observacoes || '');
  const [parcelas, setParcelas] = useState<Parcela[]>(patrocinio?.parcelas || []);
  const [contrapartidas, setContrapartidas] = useState<Contrapartida[]>(patrocinio?.contrapartidas || []);
  const [anexos, setAnexos] = useState<Anexo[]>(patrocinio?.anexos || []);
  const [subindo, setSubindo] = useState(false);

  const ehBase = moeda === config.moedaBase;
  const cotacaoEfetiva = ehBase ? 1 : (parseFloat(cotacao) || config.cotacoes[moeda] || 1);
  const totalParcelas = parcelas.reduce((s, p) => s + (Number(p.valor) || 0), 0);
  const totalContrato = parseFloat(valorTotal) || 0;
  const difere = totalContrato > 0 && parcelas.length > 0 && Math.abs(totalParcelas - totalContrato) > 0.01;

  const addParcela = () => setParcelas(p => [...p, { descricao: `${p.length + 1}ª parcela`, valor: 0, previsao: '', status: 'previsto' }]);
  const setParcela = (i: number, campo: keyof Parcela, v: string | number) =>
    setParcelas(p => p.map((x, idx) => idx === i ? { ...x, [campo]: v } : x));
  const delParcela = (i: number) => setParcelas(p => p.filter((_, idx) => idx !== i));

  const addContra = () => setContrapartidas(c => [...c, { descricao: '', tipo: 'evento', quantidade: 1, concluidas: 0, prazo: '' }]);
  const setContra = (i: number, campo: keyof Contrapartida, v: string | number) =>
    setContrapartidas(c => c.map((x, idx) => idx === i ? { ...x, [campo]: v } : x));
  const delContra = (i: number) => setContrapartidas(c => c.filter((_, idx) => idx !== i));

  const anexar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setSubindo(true);
    for (const f of files) {
      const anexo = await uploadAnexo(f, patrocinador || 'Contratos', userEmail);
      if (anexo) setAnexos(a => [...a, anexo]);
      else alert(`Não consegui subir "${f.name}".`);
    }
    setSubindo(false);
    e.target.value = '';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!patrocinador.trim()) return;
    onSave({
      patrocinador: patrocinador.trim(), contato, moeda,
      valorTotal: totalContrato, cotacao: cotacaoEfetiva,
      inicio, fim, status, fonteId, observacoes,
      parcelas: parcelas
        .filter(p => p.descricao || p.valor)
        .map(p => ({ ...p, valor: Number(p.valor) || 0, moeda: p.moeda || moeda })),
      contrapartidas: contrapartidas
        .filter(c => c.descricao)
        .map(c => ({ ...c, quantidade: Number(c.quantidade) || 1, concluidas: Number(c.concluidas) || 0 })),
      anexos,
    });
  };

  return (
    <Modal titulo={patrocinio ? 'Editar Contrato' : 'Novo Contrato'} icone={<Handshake className="w-5 h-5" />}
      largura="max-w-3xl" onClose={onClose}>
      <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Patrocinador *</label>
            <input type="text" value={patrocinador} onChange={e => setPatrocinador(e.target.value)}
              className={inputCls} placeholder="Ex: Nike" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contato</label>
            <input type="text" value={contato} onChange={e => setContato(e.target.value)}
              className={inputCls} placeholder="E-mail ou telefone" />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Moeda</label>
            <select value={moeda} onChange={e => setMoeda(e.target.value)} className={inputCls}>
              {config.moedas.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor do contrato</label>
            <input type="number" step="0.01" value={valorTotal} onChange={e => setValorTotal(e.target.value)}
              className={inputCls} placeholder="0,00" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cotação {ehBase ? '' : `(1 ${moeda} = ? ${config.moedaBase})`}
            </label>
            <input type="number" step="0.0001" value={ehBase ? 1 : cotacao} disabled={ehBase}
              onChange={e => setCotacao(e.target.value)}
              className={`${inputCls} ${ehBase ? 'bg-gray-100 text-gray-400' : ''}`}
              placeholder={String(config.cotacoes[moeda] || '')} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Situação</label>
            <select value={status} onChange={e => setStatus(e.target.value as Patrocinio['status'])} className={inputCls}>
              <option value="negociacao">Em negociação</option>
              <option value="ativo">Ativo</option>
              <option value="encerrado">Encerrado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
        </div>

        {!ehBase && totalContrato > 0 && (
          <p className="text-xs text-gray-500 -mt-2">
            Equivale a <strong>{fmt(totalContrato * cotacaoEfetiva, config.moedaBase)}</strong> na sua moeda base.
          </p>
        )}

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Início</label>
            <input type="date" value={inicio} onChange={e => setInicio(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fim</label>
            <input type="date" value={fim} onChange={e => setFim(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Entra em qual fonte</label>
            <select value={fonteId} onChange={e => setFonteId(e.target.value)} className={inputCls}>
              <option value="">Não definida</option>
              {fontes.filter(f => f.ativo !== false).map(f => <option key={f._id} value={f._id}>{f.nome}</option>)}
            </select>
          </div>
        </div>

        {/* ─── Parcelas ─── */}
        <div className="border-t border-gray-100 pt-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <label className="text-sm font-semibold text-gray-800">Quando o dinheiro entra</label>
              <p className="text-xs text-gray-500">Cada parcela vira uma conta a receber com data prevista.</p>
            </div>
            <button type="button" onClick={addParcela} className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1">
              <Plus className="w-3 h-3" /> Adicionar parcela
            </button>
          </div>
          <div className="space-y-2">
            {parcelas.map((p, i) => (
              <div key={p.id || i} className="grid grid-cols-[minmax(0,1fr)_7rem_9.5rem_auto_auto] items-center gap-2 p-2.5 bg-gray-50 rounded-xl border border-gray-100">
                <input type="text" value={p.descricao} onChange={e => setParcela(i, 'descricao', e.target.value)}
                  className={`w-full ${inputSmLivre}`} placeholder="Ex: 1ª parcela" title="Nome da parcela" />
                <input type="number" step="0.01" value={p.valor || ''} onChange={e => setParcela(i, 'valor', parseFloat(e.target.value) || 0)}
                  className={`w-full ${inputSmLivre} text-right`} placeholder="Valor" />
                <input type="date" value={p.previsao || ''} onChange={e => setParcela(i, 'previsao', e.target.value)}
                  className={`w-full ${inputSmLivre}`} />
                {p.status === 'recebido'
                  ? <span className="text-[11px] font-semibold text-green-600 w-20 text-center">recebida</span>
                  : <span className="text-[11px] text-gray-400 w-20 text-center">prevista</span>}
                <button type="button" onClick={() => delParcela(i)} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {!parcelas.length && <p className="text-xs text-gray-400 italic py-2">Nenhuma parcela ainda.</p>}
          </div>
          {difere && (
            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              As parcelas somam {fmt(totalParcelas, moeda)}, e o contrato diz {fmt(totalContrato, moeda)}.
            </p>
          )}
        </div>

        {/* ─── Contrapartidas ─── */}
        <div className="border-t border-gray-100 pt-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <label className="text-sm font-semibold text-gray-800">O que você precisa entregar</label>
              <p className="text-xs text-gray-500">Ex: comparecer a 3 eventos, postar 2 reels, usar a marca na prova.</p>
            </div>
            <button type="button" onClick={addContra} className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1">
              <Plus className="w-3 h-3" /> Adicionar contrapartida
            </button>
          </div>
          <div className="space-y-2">
            {contrapartidas.map((c, i) => (
              <div key={c.id || i} className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-xl border border-gray-100">
                <input type="text" value={c.descricao} onChange={e => setContra(i, 'descricao', e.target.value)}
                  className={`flex-1 ${inputSm}`} placeholder="Ex: Comparecer a eventos da marca" />
                <select value={c.tipo} onChange={e => setContra(i, 'tipo', e.target.value)} className={`w-32 ${inputSmLivre}`}>
                  {Object.entries(LABEL_TIPO_CONTRAPARTIDA).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <input type="number" min="1" value={c.quantidade} onChange={e => setContra(i, 'quantidade', parseInt(e.target.value) || 1)}
                  className={`w-16 ${inputSmLivre} text-center`} title="Quantas vezes" />
                <input type="date" value={c.prazo || ''} onChange={e => setContra(i, 'prazo', e.target.value)}
                  className={`w-40 ${inputSmLivre}`} title="Prazo" />
                <button type="button" onClick={() => delContra(i)} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {!contrapartidas.length && <p className="text-xs text-gray-400 italic py-2">Nenhuma contrapartida ainda.</p>}
          </div>
        </div>

        {/* ─── Anexos ─── */}
        <div className="border-t border-gray-100 pt-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <label className="text-sm font-semibold text-gray-800">Contrato e documentos</label>
              <p className="text-xs text-gray-500">O PDF do contrato fica junto do registro.</p>
            </div>
            <label className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 cursor-pointer">
              <Paperclip className="w-3 h-3" /> {subindo ? 'Enviando…' : 'Anexar arquivo'}
              <input type="file" multiple className="hidden" onChange={anexar} disabled={subindo} />
            </label>
          </div>
          <div className="space-y-1.5">
            {anexos.map((a, i) => (
              <div key={a.id || a.url || i} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-xl border border-gray-100">
                <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <a href={linkAnexo(a.url, userEmail)} target="_blank" rel="noreferrer" className="flex-1 text-sm text-blue-700 hover:underline truncate">{a.nome}</a>
                {!!a.tamanho && <span className="text-[11px] text-gray-400">{Math.round(a.tamanho / 1024)} KB</span>}
                <button type="button" onClick={() => setAnexos(x => x.filter((_, idx) => idx !== i))}
                  className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {!anexos.length && <p className="text-xs text-gray-400 italic py-2">Nenhum documento anexado.</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
          <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)}
            className={`${inputCls} resize-none`} rows={2} />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancelar</button>
          <button type="submit" className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:shadow-lg">
            Salvar Contrato
          </button>
        </div>
      </form>
    </Modal>
  );
};

/* ═══════════════════════════════════════════════════════════
   MODAL — META ANUAL
   ═══════════════════════════════════════════════════════════ */

export const MetaModal: React.FC<{
  config: FinanceConfig;
  categorias: string[];
  onSave: (dados: Record<string, unknown>) => void;
  onClose: () => void;
}> = ({ config, categorias, onSave, onClose }) => {
  const [ano, setAno] = useState(new Date().getFullYear());
  const [categoria, setCategoria] = useState('');
  const [valor, setValor] = useState('');
  const [observacoes, setObservacoes] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ ano, categoria, tipo: 'despesa', valor: parseFloat(valor) || 0, observacoes });
  };

  return (
    <Modal titulo="Meta de gasto do ano" icone={<Target className="w-5 h-5" />} onClose={onClose}>
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        <p className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-xl p-3">
          Deixe a categoria em branco para definir o teto do ano inteiro. Com categoria,
          você acompanha um limite separado (ex.: viagens).
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ano</label>
            <input type="number" value={ano} onChange={e => setAno(parseInt(e.target.value) || ano)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor ({config.moedaBase})</label>
            <input type="number" step="0.01" value={valor} onChange={e => setValor(e.target.value)}
              className={inputCls} placeholder="0,00" required />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
          <input type="text" list="cats-meta" value={categoria} onChange={e => setCategoria(e.target.value)}
            className={inputCls} placeholder="Em branco = meta do ano inteiro" />
          <datalist id="cats-meta">
            {categorias.map(c => <option key={c} value={c} />)}
          </datalist>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
          <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)}
            className={`${inputCls} resize-none`} rows={2} />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancelar</button>
          <button type="submit" className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:shadow-lg">Salvar</button>
        </div>
      </form>
    </Modal>
  );
};

/* ═══════════════════════════════════════════════════════════
   SEÇÃO — FONTES DE RECURSO
   ═══════════════════════════════════════════════════════════ */

export const SecaoFontes: React.FC<{
  saldos: SaldoFonte[];
  moedaBase: string;
  onEditar: (f: SaldoFonte) => void;
  onExcluir: (f: SaldoFonte) => void;
}> = ({ saldos, moedaBase, onEditar, onExcluir }) => {
  if (!saldos.length) {
    return <Vazio icone={<Wallet className="w-7 h-7" />} titulo="Nenhuma fonte cadastrada"
      texto="Cadastre de onde o seu dinheiro sai e entra: bolso pessoal, caixa da loja, conta do patrocinador. Depois é só apontar cada lançamento para uma delas." />;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {saldos.map(f => (
        <div key={f._id} className={`bg-white rounded-2xl border p-5 shadow-sm ${f.ativo === false ? 'opacity-60 border-gray-200' : 'border-gray-200'}`}>
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${f.cor}1a` }}>
                <Wallet className="w-5 h-5" style={{ color: f.cor }} />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 truncate">{f.nome}</p>
                <p className="text-xs text-gray-500">
                  {LABEL_TIPO_FONTE[f.tipo] || f.tipo} · {f.moeda}
                  {f.reembolsavelPadrao && <span className="text-amber-600"> · reembolsável</span>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => onEditar(f)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => onExcluir(f)} className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>

          <p className={`text-2xl font-bold ${f.saldo < 0 ? 'text-red-600' : 'text-gray-900'}`}>{fmt(f.saldo, f.moeda)}</p>
          <p className="text-xs text-gray-400 mb-3">saldo atual{f.saldoInicial ? ` (inclui ${fmt(f.saldoInicial, f.moeda)} inicial)` : ''}</p>

          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100">
            <div>
              <p className="text-[11px] text-gray-400 uppercase tracking-wide">Entrou</p>
              <p className="text-sm font-semibold text-green-600">{fmt(f.entrou, f.moeda)}</p>
              {!!f.entrouPrevisto && <p className="text-[11px] text-green-500/70">+{fmt(f.entrouPrevisto, f.moeda)} previsto</p>}
            </div>
            <div>
              <p className="text-[11px] text-gray-400 uppercase tracking-wide">Saiu</p>
              <p className="text-sm font-semibold text-red-600">{fmt(f.saiu, f.moeda)}</p>
              {!!f.saiuPrevisto && <p className="text-[11px] text-red-500/70">+{fmt(f.saiuPrevisto, f.moeda)} previsto</p>}
            </div>
          </div>
          {f.moeda !== moedaBase && (
            <p className="text-[11px] text-gray-400 mt-3 pt-3 border-t border-gray-100">
              Movimento equivalente a {fmt(f.saldoBase, moedaBase)}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   SEÇÃO — PATROCÍNIOS
   ═══════════════════════════════════════════════════════════ */

export const SecaoPatrocinios: React.FC<{
  patrocinios: Patrocinio[];
  moedaBase: string;
  onEditar: (p: Patrocinio) => void;
  onExcluir: (p: Patrocinio) => void;
  onGerarRecebiveis: (p: Patrocinio) => void;
  onMarcarContrapartida: (p: Patrocinio, c: Contrapartida, concluidas: number) => void;
  userEmail: string;
}> = ({ patrocinios, moedaBase, onEditar, onExcluir, onGerarRecebiveis, onMarcarContrapartida, userEmail }) => {
  if (!patrocinios.length) {
    return <Vazio icone={<Handshake className="w-7 h-7" />} titulo="Nenhum contrato cadastrado"
      texto="Cadastre o contrato com valor, moeda, quando cada parcela entra, o que você precisa entregar em troca e o PDF do contrato anexado." />;
  }

  return (
    <div className="space-y-4">
      {patrocinios.map(p => {
        const r = p.resumo;
        const pctRecebido = p.valorTotalBase ? Math.min(((r?.recebidoBase || 0) / p.valorTotalBase) * 100, 100) : 0;
        const parcelasPendentes = (p.parcelas || []).filter(x => !x.contaId);

        return (
          <div key={p._id} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-gray-900 text-lg">{p.patrocinador}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${CORES_STATUS_PATROCINIO[p.status] || 'bg-gray-100 text-gray-600'}`}>
                      {p.status}
                    </span>
                    {p.moeda !== moedaBase && (
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-50 text-indigo-600 flex items-center gap-1">
                        <Coins className="w-3 h-3" /> {p.moeda}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {p.inicio || p.fim ? `${fmtData(p.inicio)} → ${fmtData(p.fim)}` : 'Sem vigência definida'}
                    {p.contato ? ` · ${p.contato}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {!!parcelasPendentes.length && (
                    <button onClick={() => onGerarRecebiveis(p)}
                      className="px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg flex items-center gap-1.5"
                      title="Cria as contas a receber das parcelas que ainda não foram lançadas">
                      <Download className="w-3.5 h-3.5" /> Lançar {parcelasPendentes.length} parcela(s)
                    </button>
                  )}
                  <button onClick={() => onEditar(p)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => onExcluir(p)} className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <div>
                  <p className="text-[11px] text-gray-400 uppercase tracking-wide">Contrato</p>
                  <p className="text-sm font-bold text-gray-900">{fmt(p.valorTotal, p.moeda)}</p>
                  {p.moeda !== moedaBase && <p className="text-[11px] text-gray-400">{fmt(p.valorTotalBase, moedaBase)}</p>}
                </div>
                <div>
                  <p className="text-[11px] text-gray-400 uppercase tracking-wide">Já recebido</p>
                  <p className="text-sm font-bold text-green-600">{fmt(r?.recebidoBase || 0, moedaBase)}</p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-400 uppercase tracking-wide">A receber</p>
                  <p className="text-sm font-bold text-blue-600">{fmt(r?.aReceberBase || 0, moedaBase)}</p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-400 uppercase tracking-wide">A entregar</p>
                  <p className={`text-sm font-bold ${(r?.contrapartidasPendentes || 0) ? 'text-amber-600' : 'text-green-600'}`}>
                    {r?.contrapartidasPendentes || 0} de {r?.contrapartidasTotal || 0}
                  </p>
                </div>
              </div>

              <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all"
                  style={{ width: `${pctRecebido}%` }} />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
              {/* Parcelas */}
              <div className="p-5">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                  <CalendarClock className="w-3.5 h-3.5" /> Quando entra
                </p>
                <div className="space-y-2">
                  {(p.parcelas || []).map(parc => (
                    <div key={parc.id} className="flex items-center justify-between gap-3 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        {parc.status === 'recebido'
                          ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          : <Clock className="w-4 h-4 text-yellow-500 flex-shrink-0" />}
                        <span className="text-gray-700 truncate">{parc.descricao}</span>
                        {!parc.contaId && <span className="text-[10px] text-gray-400 border border-gray-200 rounded px-1 flex-shrink-0">não lançada</span>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-semibold text-gray-900">{fmt(parc.valor, parc.moeda || p.moeda)}</p>
                        <p className="text-[11px] text-gray-400">{fmtData(parc.previsao)}</p>
                      </div>
                    </div>
                  ))}
                  {!(p.parcelas || []).length && <p className="text-xs text-gray-400 italic">Nenhuma parcela cadastrada.</p>}
                </div>

                {!!(p.anexos || []).length && (
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                      <Paperclip className="w-3.5 h-3.5" /> Documentos
                    </p>
                    {(p.anexos || []).map(a => (
                      <a key={a.id || a.url} href={linkAnexo(a.url, userEmail)} target="_blank" rel="noreferrer"
                        className="flex items-center gap-2 text-sm text-blue-700 hover:underline py-0.5">
                        <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">{a.nome}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Contrapartidas */}
              <div className="p-5">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                  <Target className="w-3.5 h-3.5" /> O que você deve entregar
                </p>
                <div className="space-y-3">
                  {(p.contrapartidas || []).map(c => {
                    const qtd = c.quantidade || 1;
                    const feitas = c.concluidas || 0;
                    const ok = feitas >= qtd;
                    const atrasada = !ok && !!c.prazo && c.prazo < new Date().toISOString().slice(0, 10);
                    return (
                      <div key={c.id}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            {ok ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                              : atrasada ? <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                                : <Clock className="w-4 h-4 text-yellow-500 flex-shrink-0" />}
                            <span className={`text-sm truncate ${ok ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                              {c.descricao}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className={`text-xs font-semibold ${ok ? 'text-green-600' : atrasada ? 'text-red-600' : 'text-gray-600'}`}>
                              {feitas}/{qtd}
                            </span>
                            <button onClick={() => onMarcarContrapartida(p, c, feitas + 1)} disabled={ok}
                              title="Marcar mais uma entrega feita"
                              className={`px-2 py-0.5 rounded-lg text-xs font-semibold ${ok ? 'bg-gray-100 text-gray-300 cursor-not-allowed' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}>
                              +1
                            </button>
                            <button onClick={() => onMarcarContrapartida(p, c, Math.max(feitas - 1, 0))} disabled={!feitas}
                              title="Desfazer"
                              className={`p-1 rounded-lg ${feitas ? 'text-gray-400 hover:text-gray-700 hover:bg-gray-100' : 'text-gray-200 cursor-not-allowed'}`}>
                              <RotateCcw className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1 ml-6">
                          <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${ok ? 'bg-green-500' : atrasada ? 'bg-red-400' : 'bg-blue-400'}`}
                              style={{ width: `${Math.min((feitas / qtd) * 100, 100)}%` }} />
                          </div>
                          <span className="text-[11px] text-gray-400 flex-shrink-0">
                            {LABEL_TIPO_CONTRAPARTIDA[c.tipo] || c.tipo}{c.prazo ? ` · até ${fmtData(c.prazo)}` : ''}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  {!(p.contrapartidas || []).length && <p className="text-xs text-gray-400 italic">Nenhuma contrapartida cadastrada.</p>}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   SEÇÃO — REEMBOLSOS
   ═══════════════════════════════════════════════════════════ */

interface ItemReembolso {
  _id: string; descricao: string; valor: number; moeda: string;
  valorBase: number; vencimento: string; dataPagamento?: string;
  categoria: string; fonteNome?: string; reembolsado?: boolean;
  dataReembolso?: string;
}

export const SecaoReembolsos: React.FC<{
  itens: ItemReembolso[];
  resumo: { totalBase: number; quantidade: number; porFonte: Array<{ fonte: string; totalBase: number; quantidade: number; moedas: Record<string, number> }> };
  moedaBase: string;
  filtro: string;
  onFiltro: (f: string) => void;
  onQuitar: (item: ItemReembolso, desfazer: boolean) => void;
}> = ({ itens, resumo, moedaBase, filtro, onFiltro, onQuitar }) => (
  <div className="space-y-5">
    <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm font-medium text-gray-500">
            {filtro === 'quitado' ? 'Já devolvido a você' : 'Você tem a receber de volta'}
          </p>
          <p className={`text-3xl font-bold mt-1 ${filtro === 'quitado' ? 'text-gray-700' : 'text-amber-600'}`}>
            {fmt(resumo.totalBase, moedaBase)}
          </p>
          <p className="text-xs text-gray-400 mt-1">{resumo.quantidade} despesa(s)</p>
        </div>
        <div className="flex gap-2">
          {[['pendente', 'A devolver'], ['quitado', 'Devolvidos'], ['todos', 'Todos']].map(([v, l]) => (
            <button key={v} onClick={() => onFiltro(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${filtro === v ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {!!resumo.porFonte.length && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4 pt-4 border-t border-gray-100">
          {resumo.porFonte.map(f => (
            <div key={f.fonte} className="bg-amber-50/60 border border-amber-100 rounded-xl p-3">
              <p className="text-xs font-semibold text-amber-900 truncate">{f.fonte}</p>
              <p className="text-lg font-bold text-amber-700">{fmt(f.totalBase, moedaBase)}</p>
              <p className="text-[11px] text-amber-600/80">
                {Object.entries(f.moedas).map(([m, v]) => `${fmt(v, m)}`).join(' + ')} · {f.quantidade} item(ns)
              </p>
            </div>
          ))}
        </div>
      )}
    </div>

    {!itens.length ? (
      <Vazio icone={<Undo2 className="w-7 h-7" />} titulo="Nada para devolver"
        texto="Quando você pagar uma despesa com dinheiro do seu bolso, marque a fonte como reembolsável — ela aparece aqui até o dinheiro voltar." />
    ) : (
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">Despesa</th>
              <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">Saiu de</th>
              <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">Data</th>
              <th className="px-5 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wide">Valor</th>
              <th className="px-5 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wide">Ação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {itens.map(i => (
              <tr key={i._id} className={`hover:bg-gray-50 ${i.reembolsado ? 'opacity-60' : ''}`}>
                <td className="px-5 py-3">
                  <p className="text-sm font-medium text-gray-900">{i.descricao}</p>
                  {!!i.categoria && <p className="text-xs text-gray-400">{i.categoria}</p>}
                </td>
                <td className="px-5 py-3 text-sm text-gray-600">{i.fonteNome || '—'}</td>
                <td className="px-5 py-3 text-sm text-gray-600">{fmtData(i.dataPagamento || i.vencimento)}</td>
                <td className="px-5 py-3 text-right">
                  <p className="text-sm font-semibold text-gray-900">{fmt(i.valor, i.moeda)}</p>
                  {i.moeda !== moedaBase && <p className="text-[11px] text-gray-400">{fmt(i.valorBase, moedaBase)}</p>}
                </td>
                <td className="px-5 py-3 text-right">
                  {i.reembolsado ? (
                    <button onClick={() => onQuitar(i, true)}
                      className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg">
                      Reabrir
                    </button>
                  ) : (
                    <button onClick={() => onQuitar(i, false)}
                      className="px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:shadow-md rounded-lg">
                      Recebi de volta
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

/* ═══════════════════════════════════════════════════════════
   PAINEL ANUAL — bloco do topo do dashboard
   ═══════════════════════════════════════════════════════════ */

export const PainelAnual: React.FC<{
  resumo: ResumoAno | null;
  onIrPara: (secao: string) => void;
}> = ({ resumo, onIrPara }) => {
  if (!resumo) return null;
  // O painel pode estar sendo visto em outra moeda (convertida pela cotação
  // de hoje); só cai na moeda base da empresa quando nenhuma foi escolhida.
  const { patrocinios: pat, ano } = resumo;
  const moedaBase = resumo.moeda || resumo.moedaBase;

  return (
    <div className="space-y-4">
      {/* Cartões: patrocínio, reembolso, moedas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button onClick={() => onIrPara('patrocinios')}
          className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm text-left hover:border-blue-300 hover:shadow-md transition-all">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Handshake className="w-5 h-5 text-indigo-600" />
            </div>
            <span className="text-sm font-medium text-gray-500">Contratos em {ano}</span>
          </div>
          <p className="text-2xl font-bold text-indigo-600">{fmt(pat.aReceberBase, moedaBase)}</p>
          <p className="text-xs text-gray-400 mt-1">
            a receber em {ano} · {pat.ativos} contrato(s) somando {fmt(pat.contratadoBase, moedaBase)} no ano
          </p>
          {!!pat.contrapartidasPendentes && (
            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {pat.contrapartidasPendentes} contrapartida(s) a entregar
            </p>
          )}
        </button>

        <button onClick={() => onIrPara('reembolsos')}
          className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm text-left hover:border-blue-300 hover:shadow-md transition-all">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Undo2 className="w-5 h-5 text-amber-600" />
            </div>
            <span className="text-sm font-medium text-gray-500">Reembolsos</span>
          </div>
          <p className="text-2xl font-bold text-amber-600">{fmt(resumo.reembolsos.total, moedaBase)}</p>
          <p className="text-xs text-gray-400 mt-1">
            {resumo.reembolsos.quantidade
              ? `${resumo.reembolsos.quantidade} despesa(s) do seu bolso a devolver`
              : 'nada pendente do seu bolso'}
          </p>
        </button>

        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Coins className="w-5 h-5 text-emerald-600" />
            </div>
            <span className="text-sm font-medium text-gray-500">Por moeda</span>
          </div>
          {resumo.porMoeda.length ? (
            <div className="space-y-1.5">
              {resumo.porMoeda.map(m => (
                <div key={m.moeda} className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500">{m.moeda}</span>
                  <span className={`text-sm font-bold ${m.saldo < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {fmt(m.saldo, m.moeda)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400">Nenhum movimento liquidado ainda.</p>
          )}
        </div>
      </div>

      {/* Saldo por fonte + contrapartidas em aberto */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900 flex items-center gap-2"><Wallet className="w-4 h-4 text-blue-600" /> De onde saiu o dinheiro <span className="text-xs font-normal text-gray-400">· movimento de {ano}{resumo.saldoFonteAcumulado === false ? '' : ' · saldo acumulado'}</span></h3>
            <button onClick={() => onIrPara('fontes')} className="text-xs text-blue-600 hover:text-blue-800 font-semibold">ver todas</button>
          </div>
          <div className="space-y-3">
            {resumo.fontes.slice(0, 6).map(f => (
              <div key={f._id} className="flex items-center gap-3">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: f.cor }} />
                <span className="text-sm text-gray-700 flex-1 truncate">{f.nome}</span>
                <span className="text-xs text-red-500">−{fmt(f.saiu, f.moeda)}</span>
                <span className="text-xs text-green-600">+{fmt(f.entrou, f.moeda)}</span>
                <span title={f.saldoEhAcumulado === false
                    ? `Saldo fechado em ${ano} (saldo inicial + movimento do ano)`
                    : 'Saldo atual da fonte, somando todos os anos'}
                  className={`text-sm font-semibold w-28 text-right ${f.saldo < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {fmt(f.saldo, f.moeda)}
                </span>
              </div>
            ))}
            {!resumo.fontes.length && (
              <p className="text-sm text-gray-400 italic">
                Nenhuma fonte cadastrada — sem isso não dá para saber de qual bolso cada gasto saiu.
              </p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900 flex items-center gap-2"><Target className="w-4 h-4 text-amber-600" /> O que ainda devo entregar <span className="text-xs font-normal text-gray-400">· prazo em {ano}</span></h3>
            <button onClick={() => onIrPara('patrocinios')} className="text-xs text-blue-600 hover:text-blue-800 font-semibold">ver contratos</button>
          </div>
          <div className="space-y-2.5">
            {resumo.contrapartidasAbertas.slice(0, 7).map(c => {
              const atrasada = !!c.prazo && c.prazo < new Date().toISOString().slice(0, 10);
              return (
                <div key={`${c.patrocinioId}-${c.id}`} className="flex items-center gap-3">
                  {atrasada ? <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    : <Clock className="w-4 h-4 text-yellow-500 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 truncate">{c.descricao}</p>
                    <p className="text-[11px] text-gray-400">{c.patrocinador}{c.prazo ? ` · até ${fmtData(c.prazo)}` : ''}</p>
                  </div>
                  <span className={`text-xs font-semibold flex-shrink-0 ${atrasada ? 'text-red-600' : 'text-gray-600'}`}>
                    {c.concluidas}/{c.quantidade}
                  </span>
                </div>
              );
            })}
            {!resumo.contrapartidasAbertas.length && (
              <p className="text-sm text-gray-400 italic">Nada pendente — todas as contrapartidas em dia.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   CONFIGURAÇÃO DE MOEDAS
   ═══════════════════════════════════════════════════════════ */

export const MoedasConfig: React.FC<{
  config: FinanceConfig;
  onSalvar: (dados: Partial<FinanceConfig>) => void;
}> = ({ config, onSalvar }) => {
  const [base, setBase] = useState(config.moedaBase);
  const [ativas, setAtivas] = useState<string[]>(config.moedas);
  const [cotacoes, setCotacoes] = useState<Record<string, string>>(
    Object.fromEntries(Object.entries(config.cotacoes || {}).map(([k, v]) => [k, String(v)]))
  );
  const [acumulado, setAcumulado] = useState(config.saldoFonteAcumulado !== false);

  // a config chega depois do primeiro render — sincroniza quando ela troca
  React.useEffect(() => {
    setBase(config.moedaBase);
    setAtivas(config.moedas);
    setCotacoes(Object.fromEntries(Object.entries(config.cotacoes || {}).map(([k, v]) => [k, String(v)])));
    setAcumulado(config.saldoFonteAcumulado !== false);
  }, [config]);

  const alternar = (m: string) => {
    if (m === base) return; // a moeda base não pode sair da lista
    setAtivas(a => a.includes(m) ? a.filter(x => x !== m) : [...a, m]);
  };

  const salvar = () => {
    const limpas: Record<string, number> = {};
    for (const [m, v] of Object.entries(cotacoes)) {
      const n = parseFloat(v);
      if (n > 0 && m !== base) limpas[m] = n;
    }
    onSalvar({
      moedaBase: base,
      moedas: ativas.includes(base) ? ativas : [base, ...ativas],
      cotacoes: limpas,
      saldoFonteAcumulado: acumulado,
    });
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
          <Coins className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">Moedas</h3>
          <p className="text-xs text-gray-500">
            Escolha em quais moedas você lança e qual delas serve de referência para os totais.
          </p>
        </div>
      </div>

      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-700 mb-1">Moeda de referência</label>
        <select value={base} onChange={e => setBase(e.target.value)} className={`${inputCls} max-w-sm`}>
          {ativas.map(m => <option key={m} value={m}>{m} — {MOEDAS[m]?.nome || m}</option>)}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Todo total consolidado (meta do ano, saldo geral, contratos) aparece nesta moeda.
        </p>
      </div>

      <label className="block text-sm font-medium text-gray-700 mb-2">Moedas disponíveis</label>
      <div className="space-y-2">
        {Object.entries(MOEDAS).map(([codigo, info]) => {
          const ligada = ativas.includes(codigo);
          const ehBase = codigo === base;
          return (
            <div key={codigo} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${ligada ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-100'}`}>
              <button type="button" onClick={() => alternar(codigo)} disabled={ehBase}
                className={`w-10 h-6 rounded-full flex items-center px-0.5 transition-colors flex-shrink-0
                  ${ligada ? 'bg-emerald-600 justify-end' : 'bg-gray-300 justify-start'} ${ehBase ? 'opacity-60 cursor-not-allowed' : ''}`}>
                <div className="w-5 h-5 rounded-full bg-white shadow-sm" />
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800">
                  {codigo} <span className="text-gray-400 font-normal">· {info.nome}</span>
                  {ehBase && <span className="ml-2 text-[10px] font-bold text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5">referência</span>}
                </p>
              </div>
              {ligada && !ehBase && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-gray-500 whitespace-nowrap">1 {codigo} =</span>
                  <input type="number" step="0.0001" value={cotacoes[codigo] || ''}
                    onChange={e => setCotacoes(c => ({ ...c, [codigo]: e.target.value }))}
                    className="w-28 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-right"
                    placeholder="0,0000" />
                  <span className="text-xs text-gray-500">{base}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-gray-500 mt-3">
        A cotação aqui é só a <strong>sugestão inicial</strong> de cada lançamento — você pode
        trocá-la na hora de lançar, e o valor gravado guarda a cotação daquele dia.
      </p>

      {/* Como o saldo de cada caixa/fonte é lido no painel do ano */}
      <div className="mt-5 pt-5 border-t border-gray-100">
        <label className="block text-sm font-medium text-gray-700 mb-2">Saldo das fontes no painel</label>
        <div className="space-y-2">
          {[
            { valor: true, titulo: 'Acumulado (todos os anos)',
              desc: 'O caixa não zera em 1º de janeiro: mostra o que existe hoje na fonte.' },
            { valor: false, titulo: 'Somente o ano selecionado',
              desc: 'Saldo inicial + o que entrou e saiu dentro do ano escolhido no painel.' },
          ].map(op => {
            const marcada = acumulado === op.valor;
            return (
              <button key={String(op.valor)} type="button" onClick={() => setAcumulado(op.valor)}
                className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-colors
                  ${marcada ? 'border-emerald-300 bg-emerald-50/50' : 'border-gray-100 hover:border-gray-200'}`}>
                <span className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center
                  ${marcada ? 'border-emerald-600' : 'border-gray-300'}`}>
                  {marcada && <span className="w-2 h-2 rounded-full bg-emerald-600" />}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-800">{op.titulo}</span>
                  <span className="block text-xs text-gray-500">{op.desc}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end mt-4">
        <button onClick={salvar}
          className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:shadow-lg">
          Salvar moedas
        </button>
      </div>
    </div>
  );
};
